import React, { useMemo } from 'react';
import moment from 'moment';
import capitalize from 'lodash/capitalize';
import type { TableState, Column } from 'react-table';
import { useTable, useSortBy } from 'react-table';
import {
  Box,
  Heading,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
} from '../ui';
import type {
  OrderForm_OrderFragment,
  OrdersHistory_OrderFragment,
} from '../../graphql';
import { useOrderHistoriesByIdQuery } from '../../graphql';
import { isDefined } from '../../lib/util';
import { timeFieldFormat } from '../ui/TimeField';

interface ChangelogProps {
  currentOrder: OrderForm_OrderFragment;
}

interface OrderHistoryData
  extends Omit<OrdersHistory_OrderFragment, '__typename'> {
  start: number | null;
}

interface ChangelogRowData {
  date: number | null;
  user: string | null;
  changes: Array<{ key: string; value: unknown }>;
}

const humanReadableProperties: Record<string, string> = {
  cargoAmount: 'Amount',
  loadPortName: 'Load Port',
  dischargePortName: 'Discharge Port',
  assortmentName: 'Commodity',
  cargoUnitName: 'Cargo Unit',
  layday: 'Earliest loading',
  cancelling: 'Latest loading',
  rta: 'Earliest discharging',
  externalOrderId: 'External Order ID',
  orderById: 'Order detail',
};

const sortByStart = <T extends { start: number | null }>(
  rowA: T,
  rowB: T,
): number => {
  return (
    (rowA.start ?? Number.MAX_SAFE_INTEGER) -
    (rowB.start ?? Number.MAX_SAFE_INTEGER)
  );
};

const shallowDiffKeys = <T, K extends keyof T>(
  oldObj: T | undefined,
  newObj: T,
  keys: Array<K>,
): Partial<Pick<T, K>> => {
  const retObj: Partial<T> = {};

  keys.forEach((key) => {
    if (
      typeof newObj[key] !== 'undefined' &&
      newObj[key] !== (oldObj?.[key] ?? null) &&
      typeof newObj[key] !== 'object'
    ) {
      retObj[key] = newObj[key];
    }
  });

  return retObj;
};

const getTextChanges = (v: unknown) => {
  const isDate =
    typeof v === 'string' &&
    moment(v).isValid() &&
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/.test(v);

  if (isDate) {
    return timeFieldFormat(v);
  } else if (v === true) {
    return 'Yes';
  } else if (v === false) {
    return 'No';
  } else {
    return v;
  }
};

const getChanges = <
  Row extends Pick<OrderHistoryData, 'start' | 'userByModifiedByUserId'>,
  Key extends Exclude<keyof Row, 'start' | 'userByModifiedByUserId'>,
>(
  rows: Row[],
  keys: Key[],
): Array<ChangelogRowData> =>
  rows
    .map((curRow, i) => {
      const date = curRow.start;
      const user = curRow.userByModifiedByUserId?.name ?? null;

      const prevRow = i !== 0 ? rows[i - 1] : undefined;

      if (!prevRow) {
        return { date, user, changes: [{ key: '', value: 'Order created' }] };
      }

      const rowChanges = shallowDiffKeys(prevRow, curRow, keys);

      const changes = Object.entries(rowChanges).map(([key, value]) => {
        const k = key as keyof Row;
        const oldValue = prevRow ? prevRow[k] : null;
        const oldCopy = getTextChanges(oldValue);
        const curCopy = getTextChanges(value);
        return {
          key,
          value: prevRow
            ? `${oldCopy ? oldCopy + ' -> ' : ''} ${curCopy}`
            : null,
        };
      });

      if (changes.length === 0) return null;

      return { date, user, changes };
    })
    .filter(isDefined);

const OrderChangelog = ({ currentOrder }: ChangelogProps): JSX.Element => {
  const {
    loading: changelogQueryLoading,
    data: changelogQueryData,
    error: changelogQueryError,
  } = useOrderHistoriesByIdQuery({
    variables: {
      orderId: currentOrder.id,
    },
    fetchPolicy: 'no-cache',
  });

  const data = useMemo(() => {
    const currentOrderData = {
      ...currentOrder,
      orderById: {
        orderDetailsByOrderId: currentOrder.orderDetailsByOrderId,
      },
    };

    const orderHistory =
      [
        ...(changelogQueryData?.allOrdersHistories?.nodes ?? []),
        currentOrderData,
      ]
        .filter(isDefined)
        .map((x) => ({
          ...x,
          start: x.sysPeriod?.start?.value
            ? new Date(x.sysPeriod.start.value).getTime()
            : null,
          orderName:
            x.orderById?.orderDetailsByOrderId.nodes[0]?.orderName ?? '',
          loadPortName: x.locationByDischargePortId?.name ?? '',
          dischargePortName: x.locationByDischargePortId?.name ?? '',
          cargoUnitName: x.cargoUnitByCargoUnitId?.name ?? '',
          assortmentName: x.assortmentByAssortmentId?.name ?? '',
        }))
        .sort(sortByStart) ?? [];

    return getChanges(orderHistory, [
      'cargoAmount',
      'loadPortName',
      'dischargePortName',
      'assortmentName',
      'cargoUnitName',
      'layday',
      'cancelling',
      'rta',
      'externalOrderId',
      'orderName',
      'started',
      'completed',
      'archived',
      'deliveredBefore',
      'nominatedVessel',
      'shipmentId',
    ]);
  }, [changelogQueryData, currentOrder]);

  const initialState = useMemo<Partial<TableState<ChangelogRowData>>>(
    () => ({
      sortBy: [{ id: 'modifiedByUserId', desc: true }],
    }),
    [],
  );

  const columns: Column<ChangelogRowData>[] = React.useMemo(
    () => [
      {
        id: 'start',
        accessor: (row) => row.date,
        Header: 'Date',
        Cell: ({ value }: { value: number }) => <TimeField value={value} />,
      },
      {
        id: 'userId',
        accessor: (row) => row.user ?? 'User not available',
        Header: 'Modified by',
      },
      {
        id: 'changes',
        accessor: (row) => row.changes,
        Header: 'Changes',
        Cell: ({ value: rowValue }: { value: ChangelogRowData['changes'] }) => {
          return (
            <Box display="flex" flexDirection="column" flexWrap="wrap">
              {rowValue.map(({ key, value }, i) => {
                return (
                  <Text key={i} isSingleLine mr={3}>
                    <>
                      {capitalize(humanReadableProperties[key] ?? key)}
                      {key ? ': ' : ''}
                      {value}
                    </>
                  </Text>
                );
              })}
            </Box>
          );
        },
      },
    ],
    [],
  );

  const { getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<ChangelogRowData>(
      {
        columns,
        data,
        initialState,
      },
      useSortBy,
    );

  return (
    <Box m={0} p={4} height="100%" bg={`rgba(242, 244, 246, 1)`}>
      <Heading mb={4} fontWeight="bold" fontSize={4}>
        Order changelog
      </Heading>
      {changelogQueryLoading && (
        <Box display="flex" flexDirection="row" p={4}>
          <Text ml={3}>Loading order changelog</Text>
        </Box>
      )}
      {changelogQueryError && (
        <Box display="flex" flexDirection="row" p={4}>
          <Text ml={3} color="orange.orange_600">
            Error loading order changelog
          </Text>
        </Box>
      )}
      {!changelogQueryLoading && !changelogQueryError && (
        <Box borderColor="neutral.gray_225" borderWidth={1} borderStyle="solid">
          <Table>
            <thead>
              {headerGroups.map((headerGroup) => (
                <TableHeaders
                  isSticky
                  {...headerGroup.getHeaderGroupProps()}
                  key={headerGroup.getHeaderGroupProps().key}
                >
                  {headerGroup.headers.map((column) => (
                    <TableHeader
                      {...column.getHeaderProps()}
                      key={column.getHeaderProps().key}
                    >
                      {column.render('Header')}
                    </TableHeader>
                  ))}
                </TableHeaders>
              ))}
            </thead>
            <tbody {...getTableBodyProps}>
              {rows.map((row, index) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <TableRow key={key} isMuted={index % 2 !== 0} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key, ...cellProps } = cell.getCellProps();
                      return (
                        <TableCell key={key} {...cellProps}>
                          {cell.render('Cell')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default OrderChangelog;
